import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, User, AlertTriangle, Info } from "lucide-react";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useToast } from "@/hooks/use-toast";
import { getEnrollmentsByStudentId } from "@/utils/firestoreService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { subscribeToMessages } from "@/utils/firebase";

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  recipientId: string;
  content: string;
  timestamp: string;
  read: boolean;
}

interface Conversation {
  userId: string;
  name: string;
  lastMessage: string;
  timestamp: string;
  unread: boolean;
  avatar: string;
  email: string;
}

const StudentMessages = () => {
  const { toast } = useToast();
  const { currentUser, getMessages, sendMessage, checkConversationPermission, subscribeToMessages } = useFirebase();
  const location = useLocation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedInstructor, setSelectedInstructor] = useState<Conversation | null>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  useEffect(() => {
    if (location.state?.instructorId) {
      const instructorId = location.state.instructorId;
      console.log("Instructor ID passed from navigation:", instructorId);
    }
  }, [location.state]);

  useEffect(() => {
    if (!currentUser?.uid || !selectedInstructor?.userId) return;
    
    console.log("Setting up message subscription");
    const unsubscribe = subscribeToMessages(
      currentUser.uid,
      selectedInstructor.userId,
      (newMessages) => {
        console.log("Received new messages:", newMessages);
        setMessages(newMessages);
        
        if (newMessages.length > 0) {
          const lastMsg = newMessages[newMessages.length - 1];
          updateConversationLastMessage(selectedInstructor.userId, lastMsg.content, lastMsg.timestamp);
        }
      }
    );
    
    return () => {
      console.log("Cleaning up message subscription");
      unsubscribe();
    };
  }, [currentUser?.uid, selectedInstructor?.userId]);

  useEffect(() => {
    const loadConversations = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }
      
      try {
        console.log("Loading conversations for student:", currentUser.uid);
        setLoading(true);
        setPermissionError(null);
        
        const enrollments = await getEnrollmentsByStudentId(currentUser.uid);
        console.log("Fetched enrollments:", enrollments);
        
        if (enrollments.length === 0) {
          console.log("No instructors assigned to this student");
          setConversations([]);
          setPermissionError("You don't have any assigned instructors yet. An instructor must be assigned to your course enrollment before you can send messages.");
          setLoading(false);
          return;
        }
        
        const enrollmentsWithInstructor = enrollments.filter(
          enrollment => enrollment.instructorId && enrollment.instructorName
        );
        
        if (enrollmentsWithInstructor.length === 0) {
          console.log("No instructors have been assigned yet");
          setConversations([]);
          setPermissionError("No instructors have been assigned to your courses yet. Please wait for an admin to assign instructors.");
          setLoading(false);
          return;
        }
        
        console.log("Enrollments with instructors:", enrollmentsWithInstructor);
        
        const instructorConversations: Conversation[] = enrollmentsWithInstructor.map(enrollment => ({
          userId: enrollment.instructorId || "",
          name: enrollment.instructorName || "Your Instructor",
          lastMessage: "Start a conversation...",
          timestamp: new Date().toISOString(),
          unread: false,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(enrollment.instructorName || 'Instructor')}&background=random`,
          email: enrollment.instructorEmail || ""
        }));
        
        console.log("Mapped conversations:", instructorConversations);
        setConversations(instructorConversations);
        
        if (location.state?.instructorId) {
          const instructorToSelect = instructorConversations.find(
            c => c.userId === location.state.instructorId
          );
          
          if (instructorToSelect) {
            console.log("Selecting instructor from navigation:", instructorToSelect);
            setSelectedInstructor(instructorToSelect);
            loadMessages(instructorToSelect.userId);
          }
        } else if (instructorConversations.length > 0) {
          setSelectedInstructor(instructorConversations[0]);
          loadMessages(instructorConversations[0].userId);
        }
      } catch (error) {
        console.error("Error loading conversations:", error);
        toast({
          title: "Error",
          description: "Failed to load instructor conversations.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadConversations();
  }, [currentUser, toast, location.state]);

  const loadMessages = async (instructorId: string) => {
    if (!currentUser?.uid) return;
    
    try {
      setLoadingMessages(true);
      setPermissionError(null);
      
      const permissionResult = await checkConversationPermission(currentUser.uid, instructorId);
      
      if (!permissionResult.permitted) {
        console.log("Permission check failed:", permissionResult.reason);
        
        let errorMessage = permissionResult.reason || "You don't have permission to message this instructor.";
        
        if (errorMessage.includes("No instructor-student relationship")) {
          errorMessage = "You don't have permission to message this instructor. They must be assigned to one of your course enrollments by an admin.";
        } else if (errorMessage.includes("not found in database")) {
          errorMessage = "There was a problem with user information. Please contact an administrator.";
        }
        
        setPermissionError(errorMessage);
        setMessages([]);
        setLoadingMessages(false);
        return;
      }
      
      console.log("Loading messages between", currentUser.uid, "and", instructorId);
      const fetchedMessages = await getMessages(currentUser.uid, instructorId);
      console.log("Fetched messages:", fetchedMessages);
      setMessages(fetchedMessages);
      
      if (fetchedMessages.length > 0) {
        const lastMsg = fetchedMessages[fetchedMessages.length - 1];
        updateConversationLastMessage(instructorId, lastMsg.content, lastMsg.timestamp);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
      toast({
        title: "Error",
        description: "Failed to load messages.",
        variant: "destructive",
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  const updateConversationLastMessage = (instructorId: string, message: string, timestamp: string) => {
    setConversations(prev => 
      prev.map(conv => 
        conv.userId === instructorId 
          ? { ...conv, lastMessage: message, timestamp } 
          : conv
      )
    );
  };

  const handleSelectInstructor = (instructor: Conversation) => {
    setSelectedInstructor(instructor);
    loadMessages(instructor.userId);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentUser?.uid || !selectedInstructor) return;
    
    try {
      const permissionResult = await checkConversationPermission(currentUser.uid, selectedInstructor.userId);
      
      if (!permissionResult.permitted) {
        setPermissionError(permissionResult.reason || "You don't have permission to message this instructor.");
        toast({
          title: "Permission Denied",
          description: "You can only message instructors assigned to your course enrollments.",
          variant: "destructive",
        });
        return;
      }
      
      const messageData = {
        senderId: currentUser.uid,
        senderName: currentUser.displayName || "Student",
        recipientId: selectedInstructor.userId,
        content: newMessage,
        timestamp: new Date().toISOString(),
        read: false
      };
      
      console.log("Sending message:", messageData);
      const messageId = await sendMessage(messageData);
      
      const newMessageObj: Message = {
        id: messageId,
        ...messageData
      };
      
      setMessages(prev => [...prev, newMessageObj]);
      
      updateConversationLastMessage(selectedInstructor.userId, newMessage, new Date().toISOString());
      
      setNewMessage("");
    } catch (error: any) {
      console.error("Error sending message:", error);
      
      let errorMessage = "Failed to send message.";
      if (error.message && error.message.includes("Cannot send message:")) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return "Invalid time";
    }
  };

  const formatMessageDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString();
    } catch (error) {
      return "Invalid date";
    }
  };

  const renderDebugInfo = () => {
    if (!permissionError || !currentUser?.uid || !selectedInstructor) return null;
    
    return (
      <Alert className="mt-4">
        <Info className="h-4 w-4" />
        <AlertTitle>Debug Information</AlertTitle>
        <AlertDescription className="text-xs">
          Student ID: {currentUser.uid}<br />
          Instructor ID: {selectedInstructor.userId}<br />
          Error: {permissionError}
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Messages</h1>
            <p className="text-gray-500 mt-2">Communicate with your instructors</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => {
              if (currentUser?.uid) {
                toast({
                  title: "Refreshing",
                  description: "Refreshing conversation list...",
                });
                setLoading(true);
                getEnrollmentsByStudentId(currentUser.uid).then(enrollments => {
                  if (selectedInstructor) {
                    loadMessages(selectedInstructor.userId);
                  }
                });
              }
            }}
          >
            Refresh Conversations
          </Button>
        </div>

        <Card className="h-[calc(100vh-12rem)]">
          <div className="grid h-full md:grid-cols-[300px_1fr]">
            <div className="border-r">
              <CardHeader>
                <CardTitle>Instructors</CardTitle>
              </CardHeader>
              <ScrollArea className="h-[calc(100vh-16rem)]">
                <div className="space-y-2 p-4">
                  {loading ? (
                    <div className="flex justify-center py-4">
                      <p className="text-gray-500">Loading conversations...</p>
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <User className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-gray-600 font-medium">No instructors assigned</p>
                      <p className="text-xs text-gray-500 text-center mt-1">
                        Your instructors will appear here once they've been assigned by an admin.
                      </p>
                    </div>
                  ) : (
                    conversations.map((conversation) => (
                      <div
                        key={conversation.userId}
                        className={`flex items-center gap-4 p-3 rounded-lg hover:bg-accent cursor-pointer ${
                          selectedInstructor?.userId === conversation.userId
                            ? "bg-accent/50"
                            : ""
                        }`}
                        onClick={() => handleSelectInstructor(conversation)}
                      >
                        <Avatar className="h-10 w-10">
                          <img src={conversation.avatar} alt={conversation.name} />
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold truncate">{conversation.name}</h3>
                            <span className="text-xs text-gray-500">
                              {conversation.timestamp ? formatTimestamp(conversation.timestamp) : ""}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate">{conversation.lastMessage}</p>
                        </div>
                        {conversation.unread && (
                          <div className="w-2 h-2 bg-primary rounded-full" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            <div className="flex flex-col">
              <CardHeader className="border-b">
                {selectedInstructor ? (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <img src={selectedInstructor.avatar} alt={selectedInstructor.name} />
                    </Avatar>
                    <div>
                      <CardTitle>{selectedInstructor.name}</CardTitle>
                      <p className="text-xs text-gray-500">{selectedInstructor.email}</p>
                    </div>
                  </div>
                ) : (
                  <CardTitle>Select an instructor</CardTitle>
                )}
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[calc(100vh-24rem)] p-4">
                  {permissionError ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
                      <p className="text-gray-700 font-medium">{permissionError}</p>
                      {renderDebugInfo()}
                    </div>
                  ) : selectedInstructor ? (
                    loadingMessages ? (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-gray-500">Loading messages...</p>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center py-8">
                        <MessageSquare className="h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-gray-600 font-medium">No messages yet</p>
                        <p className="text-xs text-gray-500 max-w-xs mt-1">
                          Send your first message to {selectedInstructor.name} to start a conversation.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((message, index) => {
                          const isCurrentUser = message.senderId === currentUser?.uid;
                          const showDate =
                            index === 0 ||
                            formatMessageDate(message.timestamp) !==
                              formatMessageDate(messages[index - 1].timestamp);

                          return (
                            <div key={message.id} className="space-y-2">
                              {showDate && (
                                <div className="flex justify-center my-4">
                                  <div className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
                                    {formatMessageDate(message.timestamp)}
                                  </div>
                                </div>
                              )}
                              <div
                                className={`flex ${
                                  isCurrentUser ? "justify-end" : "justify-start"
                                }`}
                              >
                                <div
                                  className={`max-w-[80%] rounded-lg p-3 ${
                                    isCurrentUser
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-muted"
                                  }`}
                                >
                                  <p className="text-sm">{message.content}</p>
                                  <p
                                    className={`text-xs mt-1 ${
                                      isCurrentUser
                                        ? "text-primary-foreground/80"
                                        : "text-muted-foreground"
                                    }`}
                                  >
                                    {formatTimestamp(message.timestamp)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messageEndRef} />
                      </div>
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <MessageSquare className="h-10 w-10 text-gray-400 mb-3" />
                      <p className="text-gray-600 font-medium">Select an instructor to start messaging</p>
                      <p className="text-sm text-gray-500 max-w-xs mt-1">
                        Choose an instructor from the list to send them a message.
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
              {selectedInstructor && !permissionError && (
                <div className="p-4 border-t mt-auto">
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Type a message..."
                      className="flex-1 min-h-[60px] max-h-[120px]"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                    />
                    <Button
                      className="self-end"
                      size="icon"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudentMessages;
